import {Icon} from '../Icon';
import {cn} from '@/utils/common_functions';
import {Text} from '../Text';
import React, {
  useCallback,
  useEffect,
  useState,
  FocusEvent,
  forwardRef,
} from 'react';
import {InputStateTypes} from '@/interface';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface TimeInputInterface {
  label?: string;
  value: Date | null;
  name?: string;
  onChangeTime: (value: Date | null) => void;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  placeholder?: string;
  required?: boolean;
  className?: string;
  iconClassName?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  textClassName?: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  disablePortal?: boolean;
  minTime?: Date;
  maxTime?: Date;
}

export const TimeInput: React.FC<TimeInputInterface> = props => {
  const {
    value,
    className,
    disabled,
    error = '',
    iconClassName,
    wrapperClassName,
    inputClassName,
    textClassName,
    label,
    labelClassName,
    name,
    onChangeTime,
    placeholder = 'Select Time',
    required,
    touched,
    onBlur,
    onFocus,
    disablePortal,
    minTime,
    maxTime,
  } = props;

  const [inputState, setInputState] = useState<InputStateTypes>('default');
  
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  function onBlurHandler(e: any): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  function onFocusHandler(e: any): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  useEffect(() => {
    if (touched && error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, touched, updateInputState]);

  // Custom Input for DatePicker to maintain the same UI look
  const CustomInput = forwardRef<HTMLInputElement, any>(
    ({ value: displayValue, onClick, onChange, onBlur, onFocus, onKeyDown }, ref) => (
      <div
        onClick={!disabled ? onClick : undefined}
        className={cn(
          'border-2 flex border-ui_input_border relative text-base rounded-lg items-center w-full',
          !disabled && 'cursor-pointer',
          disabled && 'cursor-not-allowed opacity-70 border-x-ui_disabled',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          wrapperClassName,
        )}>
        <input
          ref={ref}
          value={displayValue}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly
          className={cn(
            'px-4 py-2 w-full rounded-lg self-stretch outline-none flex-1 min-w-0 bg-transparent whitespace-nowrap cursor-pointer',
            !displayValue && 'text-ui_input_border_dark',
            textClassName
          )}
        />

        <button
          type="button"
          tabIndex={-1}
          onMouseDown={e => e.preventDefault()}
          disabled={disabled}
          onClick={!disabled ? onClick : undefined}>
          <Icon
            type="calender" // assuming there is a clock icon, fallback to calender if not
            className={cn('size-4 mr-4', !disabled && 'cursor-pointer', iconClassName)}
          />
        </button>
      </div>
    )
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <Text className={cn('!font-FigtreeSemiBold inline-flex gap-1 text-ui_blue', labelClassName)} variant="body">
          {label}
          {required && <p className="text-rejection">*</p>}
        </Text>
      )}
      
      <div className="w-full">
        <DatePicker
          selected={value}
          onChange={(date: Date | null) => onChangeTime(date)}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={1}
          timeCaption="Time"
          dateFormat="h:mm aa"
          disabled={disabled}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          customInput={<CustomInput />}
          wrapperClassName="w-full"
          name={name}
          portalId={disablePortal ? undefined : "root-portal"}
          minTime={minTime}
          maxTime={maxTime}
        />
      </div>

      {touched && error && (
        <Text className="text-rejection text-sm">{error}</Text>
      )}
    </div>
  );
};
